<template>
  <div class="review-approval-editor">
    <!-- Node Intro -->
    <div class="node-intro">
      <svg preserveAspectRatio="none" width="20" overflow="visible" style="display: block;" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.66667 16.6667C4.73611 16.1806 3.14236 15.0729 1.88542 13.3438C0.628472 11.6146 0 9.69445 0 7.58333V2.5L6.66667 0L13.3333 2.5V7.58333C13.3333 8.76389 13.1319 9.89931 12.7292 10.9896C12.3264 12.0799 11.75 13.0417 11 13.875L8.33333 11.2083C8.08333 11.3611 7.81597 11.4757 7.53125 11.5521C7.24653 11.6285 6.95833 11.6667 6.66667 11.6667C5.75 11.6667 4.96528 11.3403 4.3125 10.6875C3.65972 10.0347 3.33333 9.25 3.33333 8.33333C3.33333 7.41667 3.65972 6.63195 4.3125 5.97917C4.96528 5.32639 5.75 5 6.66667 5C7.58333 5 8.36806 5.32639 9.02083 5.97917C9.67361 6.63195 10 7.41667 10 8.33333C10 8.63889 9.9618 8.93403 9.88542 9.21875C9.80903 9.50347 9.69444 9.77778 9.54167 10.0417L10.7917 11.2917C11.0694 10.7222 11.2847 10.125 11.4375 9.5C11.5903 8.875 11.6667 8.23611 11.6667 7.58333V3.64583L6.66667 1.77083L1.66667 3.64583V7.58333C1.66667 9.26389 2.13889 10.7917 3.08333 12.1667C4.02778 13.5417 5.22222 14.4583 6.66667 14.9167C7.02778 14.8056 7.37153 14.6632 7.69792 14.4896C8.02431 14.316 8.34722 14.1111 8.66667 13.875L9.83333 15.0417C9.375 15.4167 8.87847 15.7431 8.34375 16.0208C7.80903 16.2986 7.25 16.5139 6.66667 16.6667ZM6.66667 10C7.125 10 7.51736 9.83681 7.84375 9.51042C8.17014 9.18403 8.33333 8.79167 8.33333 8.33333C8.33333 7.875 8.17014 7.48264 7.84375 7.15625C7.51736 6.82986 7.125 6.66667 6.66667 6.66667C6.20833 6.66667 5.81597 6.82986 5.48958 7.15625C5.16319 7.48264 5 7.875 5 8.33333C5 8.79167 5.16319 9.18403 5.48958 9.51042C5.81597 9.83681 6.20833 10 6.66667 10Z" :fill="'#6F4999'"/>
      </svg>
      <p class="intro-text">
        <span class="intro-bold">Review & Approval</span> will allow the Actions to be executed when you reviewed and approved to process even if the Trigger grants the connected actions.
      </p>
    </div>

    <!-- AI Pre-Screening Mode -->
    <template v-if="localFormData.hasAIPreScreening">
      <!-- Section Divider -->
      <div class="section-divider">
        <div class="divider-line"></div>
        <span class="divider-text">AI Pre-Screening</span>
        <div class="divider-line"></div>
      </div>

      <!-- AI Toggle -->
      <div class="ai-toggle-section">
        <div class="toggle-row">
          <span class="toggle-label">Allow AI Pre-Screening</span>
          <div class="toggle-controls">
            <span class="toggle-text">OFF</span>
            <el-switch
              v-model="localFormData.hasAIPreScreening"
              @change="handleInput"
            />
            <span class="toggle-text">ON</span>
          </div>
        </div>
        <p class="toggle-description">
          AI can analyze workflow data and make initial approval decisions before routing to human reviewers.
        </p>
      </div>

      <!-- AI Instructions Settings -->
      <div class="ai-instructions-header">AI Instructions Settings:</div>

      <!-- All Instructions in One Purple Box -->
      <div class="ai-instructions-container">
        <!-- Approval Instructions -->
        <div class="instruction-section">
          <div class="instruction-header">
            <img src="@/assets/MyWorkBench/Workflow/approval_instruction.svg"/>
            <span class="instruction-title">Approval Instructions</span>
          </div>
          <TextArea
            v-model="localFormData.approvalInstructions"
            placeholder="Enter approval instructions"
            :max_length="1000"
            :rows="4"
            @change="handleInput"
          />
        </div>

        <!-- Rejection Instructions -->
        <div class="instruction-section">
          <div class="instruction-header">
            <img src="@/assets/MyWorkBench/Workflow/rejection_instruction.svg"/>
            <span class="instruction-title">Rejection Instructions</span>
          </div>
          <TextArea
            v-model="localFormData.rejectionInstructions"
            placeholder="Enter rejection instructions"
            :max_length="1000"
            :rows="4"
            @change="handleInput"
          />
        </div>

        <!-- Escalation Instructions -->
        <div class="instruction-section">
          <div class="instruction-header">
            <img src="@/assets/MyWorkBench/Workflow/escalation_instruction.svg"/>
            <span class="instruction-title">Escalation Instructions</span>
          </div>
          <TextArea
            v-model="localFormData.escalationInstructions"
            placeholder="Enter escalation instructions"
            :max_length="1000"
            :rows="4"
            @change="handleInput"
          />
        </div>
      </div>
    </template>

    <!-- Manual Review Mode -->
    <template v-else>
      <!-- Section Divider -->
      <div class="section-divider">
        <div class="divider-line"></div>
        <span class="divider-text">Manual Review Settings</span>
        <div class="divider-line"></div>
      </div>

      <!-- Primary Reviewer -->
      <div class="input-group">
        <div class="input-label">
          <span>Primary Reviewer</span>
        </div>
        <SingleSelector
          v-model="localFormData.primaryReviewer"
          placeholder="Please select"
          :options="primaryReviewerOptions"
          size="medium"
          @update:modelValue="handleInput"
        />
      </div>

      <!-- Enable Escalated Approval Checkbox -->
      <Checkbox
        v-model="localFormData.hasEscalation"
        built_in_theme="primary-blue"
        width="fill-whole"
        @change="handleInput"
      >
        <div class="checkbox-content">
          <span class="checkbox-title">Enable Escalated Approval</span>
          <p class="checkbox-description">Set a two-tier approval process with another reviewer.</p>
        </div>
      </Checkbox>

      <!-- Escalation Reviewer (conditional) -->
      <div v-if="localFormData.hasEscalation" class="input-group">
        <div class="input-label">
          <span>Escalation Reviewer</span>
        </div>
        <SingleSelector
          v-model="localFormData.escalationReviewer"
          placeholder="Select escalation reviewer"
          :options="escalationReviewerOptions"
          size="medium"
          @update:modelValue="handleInput"
        />
      </div>

      <!-- Review Response Time -->
      <div class="input-group">
        <div class="input-label">
          <span>Review Response Time</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="info-icon">
            <circle cx="8" cy="8" r="7" stroke="#999" stroke-width="1.5" fill="none"/>
            <path d="M8 4v4M8 10h.01" stroke="#999" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="time-input-row">
          <TextInput
            v-model="localFormData.reviewResponseTime"
            type="number"
            placeholder="Number"
            size="medium"
            customized_class="time-number-input"
            @change="handleInput"
          />
          <SingleSelector
            v-model="localFormData.reviewResponseTimeUnit"
            :options="timeUnitOptions"
            size="medium"
            customized_class="time-unit-select"
            @update:modelValue="handleInput"
          />
        </div>
      </div>

      <!-- Action on Timeout -->
      <div class="input-group">
        <div class="input-label">
          <span>Action on Timeout</span>
        </div>
        <SingleSelector
          v-model="localFormData.actionOnTimeout"
          :options="actionOnTimeoutOptions"
          placeholder="Auto Reject (Stop Workflow)"
          size="medium"
          @update:modelValue="handleInput"
        />
      </div>

      <!-- Section Divider -->
      <div class="section-divider">
        <div class="divider-line"></div>
        <span class="divider-text">AI Pre-Screening</span>
        <div class="divider-line"></div>
      </div>

      <!-- AI Toggle -->
      <div class="ai-toggle-section">
        <div class="toggle-row">
          <span class="toggle-label">Allow AI Pre-Screening</span>
          <div class="toggle-controls">
            <span class="toggle-text">OFF</span>
            <el-switch
              v-model="localFormData.hasAIPreScreening"
              @change="handleInput"
            />
            <span class="toggle-text">ON</span>
          </div>
        </div>
        <p class="toggle-description">
          AI can analyze workflow data and make initial approval decisions before routing to human reviewers.
        </p>
      </div>
    </template>

    <!-- Save Configuration Button -->
    <SaveConfigure
      :disabled="!isValid"
      @save-configuration="handleSave"
    />

    <!-- Remove Block Button -->
    <button class="remove-block-btn" @click="handleRemoveBlock">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 12.6667C4 13.4 4.6 14 5.33333 14H10.6667C11.4 14 12 13.4 12 12.6667V4.66667H4V12.6667ZM12.6667 2.66667H10.3333L9.66667 2H6.33333L5.66667 2.66667H3.33333V4H12.6667V2.66667Z" fill="#D10000"/>
      </svg>
      <span>Remove Block</span>
    </button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SaveConfigure from '@/components/workflow/SaveConfigure.vue';

export default {
  name: 'LogicReviewApprovalEditor',
  components: {
    SaveConfigure
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['save-configuration', 'remove-block'],
  data() {
    return {
      localFormData: {
        primaryReviewer: this.data?.logicCardData?.primaryReviewer || '',
        hasEscalation: this.data?.logicCardData?.hasEscalation || false,
        escalationReviewer: this.data?.logicCardData?.escalationReviewer || '',
        reviewResponseTime: this.data?.logicCardData?.reviewResponseTime || '',
        reviewResponseTimeUnit: this.data?.logicCardData?.reviewResponseTimeUnit || 'hours',
        actionOnTimeout: this.data?.logicCardData?.actionOnTimeout || 'reject',
        hasAIPreScreening: this.data?.logicCardData?.hasAIPreScreening || false,
        approvalInstructions: this.data?.logicCardData?.approvalInstructions || '',
        rejectionInstructions: this.data?.logicCardData?.rejectionInstructions || '',
        escalationInstructions: this.data?.logicCardData?.escalationInstructions || ''
      }
    };
  },
  computed: {
    ...mapGetters('login', ['userInfo']),
    ...mapGetters('workbench/providers', ['getAllProviders', 'getProviderCount']),
    
    // Get primary reviewer options (all providers)
    primaryReviewerOptions() {
      return this.getAllProviders.map(provider => ({
        label: this.formatProviderName(provider),
        value: provider.customer_id
      }));
    },
    
    // Get escalation reviewer options (exclude selected primary reviewer)
    escalationReviewerOptions() {
      return this.getAllProviders
        .filter(provider => provider.customer_id !== this.localFormData.primaryReviewer)
        .map(provider => ({
          label: this.formatProviderName(provider),
          value: provider.customer_id
        }));
    },
    
    timeUnitOptions() {
      return [
        { label: 'Hours', value: 'hours' },
        { label: 'Days', value: 'days' },
        { label: 'Weeks', value: 'weeks' },
      ];
    },
    actionOnTimeoutOptions() {
      return [
        { label: 'Auto Reject (Stop Workflow)', value: 'reject' },
        { label: 'Auto Approve (Continue Workflow)', value: 'approve' },
        { label: 'Send Reminder (Pause Workflow, extend deadline)', value: 'escalate' },
      ];
    },
    isValid() {
      if (this.localFormData.hasAIPreScreening) {
        return this.localFormData.approvalInstructions &&
               this.localFormData.rejectionInstructions &&
               this.localFormData.escalationInstructions;
      } else {
        const baseValid = this.localFormData.primaryReviewer &&
                         this.localFormData.reviewResponseTime &&
                         this.localFormData.reviewResponseTime > 0 &&
                         this.localFormData.reviewResponseTimeUnit &&
                         this.localFormData.actionOnTimeout;
        
        if (this.localFormData.hasEscalation) {
          return baseValid && this.localFormData.escalationReviewer;
        }
        
        return baseValid;
      }
    },
    printIsValid() {
      console.log('isValid', this.isValid);
      return this.isValid;
    }
  },
  watch: {
    'userInfo.clinic_id': {
      async handler(newClinicId) {
        // Fetch providers when userInfo becomes available
        if (newClinicId && this.getProviderCount === 0) {
          try {
            await this.fetchProviders(newClinicId);
            console.log('Providers fetched via watcher in LogicReviewApprovalEditor');
          } catch (error) {
            console.error('Failed to fetch providers via watcher:', error);
          }
        }
      },
      immediate: false
    }
  },
  async mounted() {
    // Initialize providers if userInfo is already available
    const clinicId = this.userInfo?.clinic_id;
    if (clinicId && this.getProviderCount === 0) {
      try {
        await this.fetchProviders(clinicId);
        console.log('Providers initialized in mounted hook');
      } catch (error) {
        console.error('Failed to initialize providers in mounted:', error);
      }
    }
    // If userInfo is not available yet, the watcher will handle it when it becomes available
  },
  methods: {
    ...mapActions('workbench/providers', ['fetchProviders']),
    
    // Format provider name: first + middle + last
    formatProviderName(provider) {
      // Check if this provider is the current user
      if (this.userInfo && provider.customer_id === this.userInfo.customer_id) {
        return 'Me (Current User)';
      }
      
      const parts = [
        provider.customer_first_name,
        provider.customer_middle_name,
        provider.customer_last_name
      ].filter(part => part && part.trim());
      return parts.join(' ');
    },
    
    handleInput() {
      // No real-time sync
    },
    handleSave() {
      if (this.isValid) {
        this.$emit('save-configuration', {
          logicCardType: 'review',
          logicCardConfigured: true,
          logicCardData: {
            primaryReviewer: this.localFormData.primaryReviewer,
            hasEscalation: this.localFormData.hasEscalation,
            escalationReviewer: this.localFormData.escalationReviewer,
            reviewResponseTime: this.localFormData.reviewResponseTime,
            reviewResponseTimeUnit: this.localFormData.reviewResponseTimeUnit,
            actionOnTimeout: this.localFormData.actionOnTimeout,
            hasAIPreScreening: this.localFormData.hasAIPreScreening,
            approvalInstructions: this.localFormData.approvalInstructions,
            rejectionInstructions: this.localFormData.rejectionInstructions,
            escalationInstructions: this.localFormData.escalationInstructions
          }
        });
      }
    },
    handleRemoveBlock() {
      this.$emit('remove-block');
    }
  }
};
</script>

<style scoped>
.review-approval-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.node-intro {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  justify-content: center;
  padding: 8px 12px;
  background: #E1D3F1;
  border-radius: 5px;
  width: 100%;
}

.intro-text {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: normal;
  color: #6F4999;
  margin: 0;
}

.intro-bold {
  font-weight: 600;
}

.section-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 2px 0;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: #999;
}

.divider-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  white-space: nowrap;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  margin-bottom: 4px;
}

.input-label {
  display: flex;
  gap: 4px;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.info-icon {
  margin-left: 4px;
}

.checkbox-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkbox-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.checkbox-description {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  margin: 0;
}

.time-input-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.time-number-input {
  width: 150px;
}

.time-unit-select {
  width: 140px;
}

.ai-toggle-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.toggle-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.toggle-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

.toggle-description {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  margin: 0;
  padding: 0 12px;
}

.ai-instructions-header {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
  margin-top: 4px;
}

.ai-instructions-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: #E1D3F1;
  width: 100%;
}

.instruction-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.instruction-header {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 24px;
}

.instruction-header img {
  width: 24px;
  height: 24px;
}

.instruction-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #6F4999;
}


.action-select {
  width: 100%;
}

:deep(.el-switch) {
  --el-switch-on-color: #6F4999;
  --el-switch-off-color: #374957;
}

.remove-block-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #D10000;
  background: #FFFFFF;
  border: 1px solid #D10000;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.remove-block-btn:hover {
  background: #F63B3B;
  color: #FFFFFF;
  border-color: #F63B3B;
}

.remove-block-btn:hover svg path {
  fill: #FFFFFF;
}

.remove-block-btn:active {
  transform: translateY(1px);
}
</style>
